import * as THREE from 'three';

/** Per-phase DOT damage per second (phases 1-6, index 0 = phase 1) */
const PHASE_DOT: readonly number[] = [1, 2, 3, 5, 7, 10];

const NOISE_SIZE = 64;

function getPhaseDot(phase: number): number {
  const idx = Math.max(0, Math.min(PHASE_DOT.length - 1, phase - 1));
  return PHASE_DOT[idx];
}

/** Procedural noise DataTexture (64×64) that animates each frame. */
function buildNoiseTexture(): THREE.DataTexture {
  const data = new Uint8Array(NOISE_SIZE * NOISE_SIZE * 4);
  const tex = new THREE.DataTexture(data, NOISE_SIZE, NOISE_SIZE, THREE.RGBAFormat);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

function updateNoiseTexture(tex: THREE.DataTexture, t: number): void {
  const data = tex.image.data as Uint8Array;
  const size = NOISE_SIZE;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const nx = x / size;
      const ny = y / size;
      // Layered sin noise for shimmer
      const v =
        0.5 +
        0.25 * Math.sin(nx * 14.1 + t * 2.3) +
        0.15 * Math.sin(ny * 9.7 + t * 3.1) +
        0.1 * Math.sin((nx + ny) * 18.3 + t * 1.7);
      const byte = Math.floor(Math.max(0, Math.min(1, v)) * 255);
      data[i] = byte;         // R
      data[i + 1] = 0;        // G
      data[i + 2] = byte;     // B
      data[i + 3] = byte;     // A (used as emissive intensity)
    }
  }
  tex.needsUpdate = true;
}

export class FpsStormCircle {
  private readonly scene: THREE.Scene;
  private readonly mount: HTMLElement;

  // HUD: countdown timer
  private readonly hudTimer: HTMLDivElement;

  // HUD: purple vignette overlay
  private readonly vignetteEl: HTMLDivElement;

  // Border particles container
  private readonly particleContainer: HTMLDivElement;
  private readonly particles: HTMLDivElement[] = [];
  private readonly PARTICLE_COUNT = 20;
  private particleTimers: number[] = [];

  // Storm wall shimmer plane (overlay on wall)
  private shimmerMesh: THREE.Mesh<THREE.CylinderGeometry, THREE.MeshStandardMaterial> | null = null;
  private noiseTexture: THREE.DataTexture;
  private shimmerRadius = 0;
  private shimmerCenterX = 0;
  private shimmerCenterZ = 0;

  // State
  private currentCircle = { cx: 0, cz: 0, r: 100 };
  private nextCircle = { cx: 0, cz: 0, r: 80 };
  private time = 0;
  private vignetteAlpha = 0;
  private pulseT = 0;

  constructor(scene: THREE.Scene, mount: HTMLElement) {
    this.scene = scene;
    this.mount = mount;

    // --- Countdown HUD ---
    this.hudTimer = document.createElement('div');
    this.hudTimer.style.cssText = [
      'position:fixed',
      'top:80px',
      'left:50%',
      'transform:translateX(-50%)',
      'color:#e060ff',
      'font-family:"Segoe UI",Arial,sans-serif',
      'font-size:15px',
      'font-weight:700',
      'letter-spacing:0.06em',
      'text-shadow:0 0 8px #c030ff,0 0 2px #000',
      'pointer-events:none',
      'z-index:9995',
      'display:none',
    ].join(';');
    this.mount.appendChild(this.hudTimer);

    // --- Vignette overlay ---
    this.vignetteEl = document.createElement('div');
    this.vignetteEl.style.cssText = [
      'position:fixed',
      'inset:0',
      'pointer-events:none',
      'z-index:9990',
      'box-shadow:inset 0 0 120px rgba(180,40,255,0)',
      'transition:none',
    ].join(';');
    this.mount.appendChild(this.vignetteEl);

    // --- Border particles ---
    this.particleContainer = document.createElement('div');
    this.particleContainer.style.cssText = [
      'position:fixed',
      'inset:0',
      'pointer-events:none',
      'z-index:9991',
    ].join(';');
    this.mount.appendChild(this.particleContainer);

    for (let i = 0; i < this.PARTICLE_COUNT; i++) {
      const p = document.createElement('div');
      p.style.cssText = [
        'position:absolute',
        'width:6px',
        'height:6px',
        'border-radius:50%',
        'background:rgba(200,80,255,0.85)',
        'box-shadow:0 0 6px #cc44ff',
        'pointer-events:none',
        'opacity:0',
      ].join(';');
      this.particleContainer.appendChild(p);
      this.particles.push(p);
      this.particleTimers.push(Math.random() * 2);
    }

    // --- Shimmer noise texture ---
    this.noiseTexture = buildNoiseTexture();
  }

  /** Main update — call every frame from the game loop. */
  update(
    dt: number,
    stormRadius: number,
    stormCenter: { x: number; z: number },
    stormPhase: number,
    playerX: number,
    playerZ: number,
    isPlayerOutside: boolean,
    shrinkEta: number | null,
  ): void {
    this.time += dt;
    this.pulseT += dt;

    // Update circle data for minimap use
    this.currentCircle = { cx: stormCenter.x, cz: stormCenter.z, r: stormRadius };
    // Next circle approximation — slightly smaller, same center (minimap will use this)
    this.nextCircle = { cx: stormCenter.x, cz: stormCenter.z, r: Math.max(10, stormRadius * 0.75) };

    // --- Phase DOT scaling (informational — exposed for game to use) ---
    const _dot = getPhaseDot(stormPhase); void _dot;

    // --- Countdown HUD ---
    if (shrinkEta !== null && shrinkEta > 0) {
      const secs = Math.ceil(shrinkEta);
      this.hudTimer.textContent = `STORM CLOSES IN: ${secs}s`;
      this.hudTimer.style.display = 'block';
    } else {
      this.hudTimer.style.display = 'none';
    }

    // --- Vignette ---
    if (isPlayerOutside) {
      const pulse = 0.45 + Math.sin(this.pulseT * 3.2) * 0.12 + Math.sin(this.pulseT * 7.1) * 0.06;
      this.vignetteAlpha = Math.min(1, this.vignetteAlpha + dt * 2.5);
      const a = this.vignetteAlpha * pulse;
      this.vignetteEl.style.boxShadow = `inset 0 0 120px rgba(180,40,255,${a.toFixed(3)})`;
    } else {
      this.vignetteAlpha = Math.max(0, this.vignetteAlpha - dt * 3.5);
      if (this.vignetteAlpha <= 0) {
        this.vignetteEl.style.boxShadow = 'inset 0 0 120px rgba(180,40,255,0)';
      } else {
        this.vignetteEl.style.boxShadow = `inset 0 0 120px rgba(180,40,255,${this.vignetteAlpha.toFixed(3)})`;
      }
    }

    // --- DOT border particles ---
    if (isPlayerOutside) {
      this.particleContainer.style.display = 'block';
      this._updateParticles(dt);
    } else {
      this.particleContainer.style.display = 'none';
    }

    // --- Storm wall shimmer ---
    this._updateShimmer(dt, stormRadius, stormCenter);
  }

  private _updateParticles(dt: number): void {
    const W = window.innerWidth;
    const H = window.innerHeight;
    for (let i = 0; i < this.PARTICLE_COUNT; i++) {
      this.particleTimers[i] -= dt;
      if (this.particleTimers[i] <= 0) {
        // Respawn on random screen border
        const side = Math.floor(Math.random() * 4);
        let px = 0, py = 0;
        if (side === 0) { px = Math.random() * W; py = Math.random() * 40; }
        else if (side === 1) { px = Math.random() * W; py = H - Math.random() * 40; }
        else if (side === 2) { px = Math.random() * 40; py = Math.random() * H; }
        else { px = W - Math.random() * 40; py = Math.random() * H; }

        const p = this.particles[i];
        p.style.left = `${px}px`;
        p.style.top = `${py}px`;
        p.style.opacity = `${0.6 + Math.random() * 0.4}`;
        const size = 4 + Math.random() * 6;
        p.style.width = `${size}px`;
        p.style.height = `${size}px`;
        this.particleTimers[i] = 0.1 + Math.random() * 0.5;
      }
      // Fade out over lifetime
      const opacity = parseFloat(this.particles[i].style.opacity || '0');
      this.particles[i].style.opacity = `${Math.max(0, opacity - dt * 1.2)}`;
    }
  }

  private _updateShimmer(dt: number, stormRadius: number, stormCenter: { x: number; z: number }): void {
    const safeR = stormRadius * 0.95;
    const cx = stormCenter.x;
    const cz = stormCenter.z;

    // Create or update shimmer cylinder
    const radiusChanged = Math.abs(safeR - this.shimmerRadius) > 0.5
      || Math.abs(cx - this.shimmerCenterX) > 0.5
      || Math.abs(cz - this.shimmerCenterZ) > 0.5;

    if (!this.shimmerMesh) {
      const mat = new THREE.MeshStandardMaterial({
        transparent: true,
        opacity: 0.28,
        side: THREE.DoubleSide,
        depthWrite: false,
        emissive: new THREE.Color(0xcc44ff),
        emissiveIntensity: 0.6,
        emissiveMap: this.noiseTexture,
        roughness: 1,
        metalness: 0,
      });
      const geo = new THREE.CylinderGeometry(safeR, safeR, 52, 96, 1, true);
      this.shimmerMesh = new THREE.Mesh(geo, mat);
      this.shimmerMesh.position.set(cx, 26, cz);
      this.shimmerMesh.renderOrder = 4;
      this.scene.add(this.shimmerMesh);
      this.shimmerRadius = safeR;
      this.shimmerCenterX = cx;
      this.shimmerCenterZ = cz;
    } else if (radiusChanged) {
      this.shimmerMesh.geometry.dispose();
      this.shimmerMesh.geometry = new THREE.CylinderGeometry(safeR, safeR, 52, 96, 1, true);
      this.shimmerMesh.position.set(cx, 26, cz);
      this.shimmerRadius = safeR;
      this.shimmerCenterX = cx;
      this.shimmerCenterZ = cz;
    } else {
      this.shimmerMesh.position.set(cx, 26, cz);
    }

    // Animate noise texture
    updateNoiseTexture(this.noiseTexture, this.time);

    // Pulse emissive intensity
    const pulse = 0.5 + Math.sin(this.time * 2.8) * 0.3 + Math.sin(this.time * 6.1) * 0.1;
    if (this.shimmerMesh) {
      this.shimmerMesh.material.emissiveIntensity = pulse;
    }
  }

  getCurrentCircleData(): { cx: number; cz: number; r: number } {
    return { ...this.currentCircle };
  }

  getNextCircleData(): { cx: number; cz: number; r: number } {
    return { ...this.nextCircle };
  }

  /** Returns the scaled DOT for the given phase (utility). */
  static getPhaseDamagePerSecond(phase: number): number {
    return getPhaseDot(phase);
  }

  dispose(): void {
    this.hudTimer.remove();
    this.vignetteEl.remove();
    this.particleContainer.remove();
    if (this.shimmerMesh) {
      this.scene.remove(this.shimmerMesh);
      this.shimmerMesh.geometry.dispose();
      (this.shimmerMesh.material as THREE.MeshStandardMaterial).dispose();
      this.shimmerMesh = null;
    }
    this.noiseTexture.dispose();
  }
}
